import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { useViewMode } from '../contexts/ViewModeContext';
import { usePanelNavigation, usePanelPreferenceStore, usePanelScopeRegistry } from '../stores';

import type { MouseEvent } from 'react';
import type { PanelId } from '../stores/usePanelNavigation';
import type { LayoutPreferenceType } from '../stores/usePanelPreferenceStore';
import type { PanelScopeConfig, PanelScopeKey } from '../stores/usePanelScopeRegistry';

// ============================================================================
// Types
// ============================================================================

/**
 * usePanelLinkRouter 옵션
 */
export interface UsePanelLinkRouterOptions {
  /**
   * PanelPreferenceStore 조회용 레이아웃 타입
   * @default 'detail'
   */
  layoutType?: LayoutPreferenceType;

  /**
   * embedded 컨텍스트에서 패널 대신 router.push로 이동할지 여부
   * @default true
   */
  preventWhenEmbedded?: boolean;

  /**
   * PanelScopeRegistry에 등록된 기본 scopeKey (선택)
   *
   * @note 각 호출마다 PanelLinkParams.scopeKey로 덮어쓸 수 있습니다.
   */
  scopeKey?: PanelScopeKey;
}

/**
 * 패널 링크 파라미터
 */
export interface PanelLinkParams {
  /**
   * 패널 ID (단일 또는 다중)
   * @example 'zone-123' | ['zone-1', 'zone-2']
   */
  panelId: PanelId;

  /**
   * 링크 href (resolvePanelHref가 없을 때 사용)
   */
  href?: string;

  /**
   * 패널 scope 키 (옵션의 scopeKey를 덮어씁니다)
   */
  scopeKey?: PanelScopeKey;
}

/**
 * usePanelLinkRouter 반환 타입
 */
export interface PanelLinkRouterReturn {
  /**
   * 패널을 직접 여는 함수
   *
   * @note embedded 컨텍스트에서 preventWhenEmbedded가 true면 router.push로 이동
   */
  openPanel: (params: PanelLinkParams) => void;

  /**
   * 클릭 이벤트와 함께 패널을 여는 함수
   *
   * @note metaKey/ctrlKey가 눌린 경우 기본 동작(새 탭 열기) 유지
   */
  openPanelByClick: (event: MouseEvent, params: PanelLinkParams) => void;

  /**
   * 패널을 열 수 있는 상태인지 여부
   *
   * @note 다음 조건을 모두 만족해야 true:
   * - viewMode === 'standalone'
   * - PanelPreferenceStore.preferences[layoutType].enabled === true
   * - PanelPreferenceStore.availability[layoutType] === true
   */
  shouldOpenPanel: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * 패널 href를 해석합니다.
 *
 * 우선순위:
 * 1. PanelScopeConfig.resolvePanelHref (Registry 설정)
 * 2. PanelLinkParams.href (호출 시 명시)
 * 3. router.asPath (현재 경로)
 */
function resolveHref(
  params: PanelLinkParams,
  scopeKey: PanelScopeKey,
  config: PanelScopeConfig | undefined,
  currentPath: string,
): string {
  // Registry에 resolvePanelHref가 있으면 사용
  if (config?.resolvePanelHref) {
    return config.resolvePanelHref({ scopeKey, panelId: params.panelId });
  }

  // 호출 시 명시된 href 사용
  if (params.href) {
    return params.href;
  }

  // 현재 경로 반환
  return currentPath;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * V2 패널 링크 오픈 헬퍼 훅
 *
 * `usePanelRouter`와 동일한 사용성(`openPanel`, `openPanelByClick`, `shouldOpenPanel`)을
 * 제공하되, V2 스택(PanelNavigation, PanelPreferenceStore, ViewMode, Registry)에 맞게 동작합니다.
 *
 * @note
 * - metaKey/ctrlKey 클릭 시 새 창 허용
 * - embedded 모드에서는 router.push로 페이지 이동 (패널 중첩 방지)
 * - standalone 모드에서만 패널 오픈
 *
 * @example
 * ```tsx
 * const { openPanelByClick } = usePanelLinkRouter();
 *
 * return (
 *   <NameCell
 *     name={record.name}
 *     href={`/dns/zones/${zoneId}/records/${recordId}`}
 *     onNameClick={event =>
 *       openPanelByClick(event, {
 *         scopeKey: RECORD_DETAIL_SCOPE.scopeKey,
 *         panelId: `${zoneId}/${recordId}`,
 *         href: `/dns/zones/${zoneId}/records/${recordId}`,
 *       })
 *     }
 *   />
 * );
 * ```
 *
 * @example 기본 scopeKey 설정
 * ```tsx
 * const { openPanelByClick } = usePanelLinkRouter({
 *   scopeKey: ZONE_LIST_SCOPE.scopeKey,
 * });
 *
 * // scopeKey 생략 가능
 * openPanelByClick(event, { panelId: zoneId });
 * ```
 */
const usePanelLinkRouter = (options?: UsePanelLinkRouterOptions): PanelLinkRouterReturn => {
  const layoutType = options?.layoutType ?? 'detail';
  const preventWhenEmbedded = options?.preventWhenEmbedded ?? true;

  // V2 stores
  const preferences = usePanelPreferenceStore(state => state.preferences[layoutType]);
  const availability = usePanelPreferenceStore(state => state.availability[layoutType]);
  const viewMode = useViewMode();
  const router = useRouter();
  const getScopeConfig = usePanelScopeRegistry(state => state.getScopeConfig);
  // Phase 6: openDetailPanel → openSidePanel 리네이밍
  const openSidePanel = usePanelNavigation(state => state.openSidePanel);

  /**
   * scopeKey 해석 (params > options)
   */
  const resolveScopeKey = useCallback(
    (params: PanelLinkParams): PanelScopeKey | undefined => {
      return params.scopeKey ?? options?.scopeKey;
    },
    [options?.scopeKey],
  );

  /**
   * 패널을 열 수 있는 조건 확인
   *
   * - standalone 모드에서만 패널 열기 가능
   * - PreferenceStore에서 enabled 및 availability 확인
   */
  const shouldOpenPanel = viewMode === 'standalone' && preferences.enabled && availability;

  /**
   * 패널을 직접 여는 함수
   */
  const openPanel = useCallback(
    (params: PanelLinkParams) => {
      const scopeKey = resolveScopeKey(params);
      if (!scopeKey) {
        console.warn('[usePanelLinkRouter] scopeKey is required');
        return;
      }

      const config = getScopeConfig(scopeKey);
      if (!config?.panelResource) {
        console.warn(`[usePanelLinkRouter] No panelResource for scope: ${scopeKey}`);
        return;
      }

      // embedded 컨텍스트에서는 router.push로 이동
      if (preventWhenEmbedded && viewMode === 'embedded') {
        const href = resolveHref(params, scopeKey, config, router.asPath);
        router.push(href);
        return;
      }

      // 패널을 열 수 없는 상태면 종료
      if (!shouldOpenPanel) {
        return;
      }

      // V2 PanelNavigation으로 패널 열기
      // Phase 6: openDetailPanel → openSidePanel 리네이밍
      openSidePanel({
        scopeKey,
        panelResource: config.panelResource,
        panelId: params.panelId,
        viewMode: 'standalone',
      });
    },
    [
      resolveScopeKey,
      getScopeConfig,
      preventWhenEmbedded,
      viewMode,
      router,
      shouldOpenPanel,
      openSidePanel,
    ],
  );

  /**
   * 클릭 이벤트와 함께 패널을 여는 함수
   */
  const openPanelByClick = useCallback(
    (event: MouseEvent, params: PanelLinkParams) => {
      // metaKey/ctrlKey가 눌린 경우 새 탭 열기 허용
      if (event.metaKey || event.ctrlKey) {
        return;
      }

      const scopeKey = resolveScopeKey(params);
      if (!scopeKey) {
        console.warn('[usePanelLinkRouter] scopeKey is required');
        return;
      }

      const config = getScopeConfig(scopeKey);
      if (!config?.panelResource) {
        console.warn(`[usePanelLinkRouter] No panelResource for scope: ${scopeKey}`);
        return;
      }

      const href = resolveHref(params, scopeKey, config, router.asPath);

      // embedded 컨텍스트에서는 router.push로 이동
      if (preventWhenEmbedded && viewMode === 'embedded') {
        event.preventDefault();
        router.push(href);
        return;
      }

      // 패널을 열 수 없는 상태면 기본 링크 동작 유지
      if (!shouldOpenPanel) {
        return;
      }

      // 기본 동작 방지 후 패널 열기
      // Phase 6: openDetailPanel → openSidePanel 리네이밍
      event.preventDefault();
      openSidePanel({
        scopeKey,
        panelResource: config.panelResource,
        panelId: params.panelId,
        viewMode: 'standalone',
      });
    },
    [
      resolveScopeKey,
      getScopeConfig,
      router,
      preventWhenEmbedded,
      viewMode,
      shouldOpenPanel,
      openSidePanel,
    ],
  );

  return {
    shouldOpenPanel,
    openPanel,
    openPanelByClick,
  };
};

export default usePanelLinkRouter;
